import { useEffect, useMemo, useRef } from 'react';
import type { Map as LeafletMap, Marker, LeafletMouseEvent } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../ui/button';

interface LocationFields {
  locationName?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

interface PinValue {
  latitude?: number;
  longitude?: number;
}

const DEFAULT_CENTER: [number, number] = [39.8283, -98.5795];

const queryFor = (locationName?: string, address?: string) =>
  [locationName, address].filter(Boolean).join(', ');

const mapSearchUrl = (query: string) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

const embedUrl = (query: string) =>
  `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;

type LeafletModule = typeof import('leaflet');

function pinIcon(L: LeafletModule) {
  return L.divIcon({
    className: '',
    html:
      '<div style="width:22px;height:22px;border-radius:9999px;background:#ef4444;border:3px solid white;box-shadow:0 10px 24px rgba(0,0,0,.45);transform:translate(-2px,-2px)"></div>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

export function LocationMap({
  locationName,
  address,
  latitude,
  longitude,
}: LocationFields) {
  const query = queryFor(locationName, address);
  const hasPin = typeof latitude === 'number' && typeof longitude === 'number';
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletRef = useRef<LeafletMap>();
  const markerRef = useRef<Marker>();
  const leafletModuleRef = useRef<LeafletModule>();

  useEffect(() => {
    if (!hasPin || !mapRef.current) return;
    let cancelled = false;
    void (async () => {
      const L = await import('leaflet');
      if (cancelled || !mapRef.current) return;
      leafletModuleRef.current = L;
      const center: [number, number] = [latitude, longitude];
      const map = L.map(mapRef.current, {
        center,
        zoom: 16,
        scrollWheelZoom: false,
        dragging: false,
        touchZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomControl: false,
      });
      leafletRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);
      markerRef.current = L.marker(center, { icon: pinIcon(L) }).addTo(map);
      setTimeout(() => map.invalidateSize(), 0);
    })();
    return () => {
      cancelled = true;
      leafletRef.current?.remove();
      leafletRef.current = undefined;
      markerRef.current = undefined;
      leafletModuleRef.current = undefined;
    };
  }, [hasPin, latitude, longitude]);

  if (hasPin) {
    const pinQuery = `${latitude},${longitude}`;
    return (
      <div className="space-y-3">
        <div
          ref={mapRef}
          className="h-64 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
        />
        <Button href={mapSearchUrl(pinQuery)} target="_blank" rel="noreferrer" outline>
          Open map
        </Button>
      </div>
    );
  }

  if (!query) return null;

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        <iframe
          title={query}
          src={embedUrl(query)}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-64 w-full border-0"
        />
      </div>
      <Button href={mapSearchUrl(query)} target="_blank" rel="noreferrer" outline>
        Open map
      </Button>
    </div>
  );
}

export function LocationPinPicker({
  value,
  onChange,
}: {
  value: PinValue;
  onChange: (value: Required<PinValue>) => void;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletRef = useRef<LeafletMap>();
  const markerRef = useRef<Marker>();
  const leafletModuleRef = useRef<LeafletModule>();
  const hasPin = typeof value.latitude === 'number' && typeof value.longitude === 'number';
  const center = useMemo<[number, number]>(
    () => (hasPin ? [value.latitude!, value.longitude!] : DEFAULT_CENTER),
    [hasPin, value.latitude, value.longitude],
  );

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;
    void (async () => {
      const L = await import('leaflet');
      if (cancelled || !mapRef.current) return;
      leafletModuleRef.current = L;
      const map = L.map(mapRef.current, {
        center,
        zoom: hasPin ? 16 : 4,
        scrollWheelZoom: false,
      });
      leafletRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);
      if (hasPin) markerRef.current = L.marker(center, { icon: pinIcon(L) }).addTo(map);
      map.on('click', (event: LeafletMouseEvent) => {
        const next = {
          latitude: Number(event.latlng.lat.toFixed(6)),
          longitude: Number(event.latlng.lng.toFixed(6)),
        };
        onChange(next);
      });
      setTimeout(() => map.invalidateSize(), 0);
    })();
    return () => {
      cancelled = true;
      leafletRef.current?.remove();
      leafletRef.current = undefined;
      markerRef.current = undefined;
      leafletModuleRef.current = undefined;
    };
    // Intentionally create the map once per mount; the second effect syncs pin changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = leafletRef.current;
    const L = leafletModuleRef.current;
    if (!map || !hasPin) return;
    const next: [number, number] = [value.latitude!, value.longitude!];
    if (!markerRef.current) {
      if (!L) return;
      markerRef.current = L.marker(next, { icon: pinIcon(L) }).addTo(map);
    } else {
      markerRef.current.setLatLng(next);
    }
    map.setView(next, Math.max(map.getZoom(), 15));
  }, [hasPin, value.latitude, value.longitude]);

  return (
    <div
      ref={mapRef}
      className="h-72 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950"
    />
  );
}
